import { useState, useEffect } from 'react';
import { Calendar, Send, Clock, CheckCircle, XCircle, AlertCircle, Coffee } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const DemandesPage = ({ user }) => {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [quota, setQuota] = useState({ month: '', used: 0, limit: 2, remaining: 2, message: 'Demandes utilisées : 0/2' });

  const getCanCancelDemande = (demande) => {
    if (!demande || demande.status !== 'pending') return false;
    if (!demande.created_at) return false;
    const createdAt = new Date(demande.created_at);
    const now = new Date();
    return (now.getTime() - createdAt.getTime()) <= (24 * 60 * 60 * 1000);
  };

  // RÈGLE: Les demandes doivent être faites au moins 2 SEMAINES à l'avance
  // Get next 4 weeks dates (starting from 2 weeks from now)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    // Start from the Monday of the week that is 2 weeks from now (14 days minimum)
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(today.getDate() + 14);
    
    // Find the Monday of that week
    const dayOfWeek = twoWeeksFromNow.getDay();
    const daysToMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
    const startDate = new Date(twoWeeksFromNow);
    startDate.setDate(twoWeeksFromNow.getDate() + daysToMonday);
    
    // Generate 4 weeks of dates
    for (let i = 0; i < 28; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const availableDates = getAvailableDates();

  useEffect(() => {
    fetchDemandes();
    fetchQuota();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchQuota(selectedDate.slice(0, 7));
    }
  }, [selectedDate]);

  const fetchDemandes = async () => {
    try {
      const response = await axios.get(`${API}/demandes/user/${user.user_id}`, {
        withCredentials: true
      });
      
      // Filter out demandes where ALL dates are in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const activeDemandes = response.data.filter(demande => {
        // Keep demande if at least one date is today or in the future
        return demande.dates.some(dateStr => {
          const demandeDate = new Date(dateStr);
          demandeDate.setHours(0, 0, 0, 0);
          return demandeDate >= today;
        });
      });
      
      setDemandes(activeDemandes);
    } catch (error) {
      console.error('Failed to fetch demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuota = async (targetMonth) => {
    try {
      const monthParam = targetMonth ? `?month=${targetMonth}` : '';
      const response = await axios.get(`${API}/demandes/quota${monthParam}`, {
        withCredentials: true
      });
      setQuota(response.data || { month: '', used: 0, limit: 2, remaining: 2, message: 'Demandes utilisées : 0/2' });
    } catch (error) {
      console.error('Failed to fetch quota:', error);
    }
  };

  const toggleDate = (date) => {
    setSelectedDate(date);
  };

  const submitDemande = async () => {
    setError('');
    setSuccess('');
    
    if (!selectedDate) {
      setError('Veuillez sélectionner une date');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/demandes`, {
        dates: [selectedDate],
        reason: reason || 'Demande de repos'
      }, { withCredentials: true });
      
      setSuccess('✅ Demande envoyée avec succès !');
      setSelectedDate('');
      setReason('');
      fetchDemandes();
      fetchQuota();
    } catch (error) {
      console.error('Failed to submit demande:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'envoi de la demande';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelDemande = async (demandeId) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API}/demandes/${demandeId}/cancel`, {}, { withCredentials: true });
      setSuccess('✅ Demande annulée avec succès.');
      fetchDemandes();
      fetchQuota();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'annulation de la demande';
      setError(errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'cancelled': return <XCircle className="w-5 h-5 text-slate-500" />;
      case 'partial': return <Clock className="w-5 h-5 text-indigo-500" />;
      default: return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Validée';
      case 'rejected': return 'Refusée';
      case 'cancelled': return 'Annulée';
      case 'partial': return 'Modifiée';
      default: return 'En attente';
    }
  };

  // Get individual date status
  const getDateStatus = (demande, date) => {
    if (demande.dates_status && demande.dates_status[date]) {
      return demande.dates_status[date];
    }
    // Fallback to global status for old demandes
    return demande.status === 'approved' ? 'approved' : 
           demande.status === 'rejected' ? 'rejected' : 'pending';
  };

  const getDateStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
      case 'rejected': return <XCircle className="w-3.5 h-3.5 text-red-500" />;
      case 'cancelled': return <XCircle className="w-3.5 h-3.5 text-slate-500" />;
      case 'partial': return <Clock className="w-3.5 h-3.5 text-indigo-500" />;
      default: return <Clock className="w-3.5 h-3.5 text-yellow-500" />;
    }
  };

  const getDateStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Validé';
      case 'rejected': return 'Refusé';
      case 'cancelled': return 'Annulé';
      case 'partial': return 'Modifié';
      default: return 'En attente';
    }
  };

  // Group available dates by week
  const groupedDates = availableDates.reduce((acc, date) => {
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1));
    const weekKey = weekStart.toISOString().split('T')[0];
    
    if (!acc[weekKey]) acc[weekKey] = [];
    acc[weekKey].push(date);
    return acc;
  }, {});

  const selectedMonth = selectedDate ? selectedDate.slice(0, 7) : null;
  const isQuotaReachedForSelection = !!selectedMonth && selectedMonth === quota.month && quota.used >= quota.limit;

  return (
    <Layout user={user}>
      <div className="space-y-6 fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">Demandes de repos</h1>
          <p className="text-gray-500">Demandez vos jours de repos à l&apos;avance</p>
        </div>

        {/* New Request */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coffee className="w-5 h-5" />
              Nouvelle demande
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-4">
              Sélectionnez une seule journée de repos (minimum 2 semaines à l&apos;avance). Pour deux jours, créez deux demandes.
            </p>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                ❌ {error}
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm">
                {success}
              </div>
            )}

            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="employee-demandes-quota-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-indigo-800">Quota mensuel</p>
                  <p className="text-xs text-indigo-700">{quota.message || `Demandes utilisées : ${quota.used}/2`}</p>
                </div>
                <span className="text-base font-bold text-indigo-700" data-testid="employee-demandes-quota-counter">{quota.used}/{quota.limit}</span>
              </div>
            </div>

            {/* Date selection by week */}
            <div className="space-y-4 mb-6">
              {Object.entries(groupedDates).map(([weekStart, dates]) => (
                <div key={weekStart} className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    Semaine du {new Date(weekStart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                  </p>
                  <div className="grid grid-cols-7 gap-1">
                    {dates.map(date => {
                      const isSelected = selectedDate === date;
                      const dayName = new Date(date).toLocaleDateString('fr-FR', { weekday: 'short' });
                      const dayNum = new Date(date).getDate();
                      
                      return (
                        <button
                          key={date}
                          onClick={() => toggleDate(date)}
                          data-testid={`employee-demandes-date-button-${date}`}
                          className={`p-2 rounded-lg text-center transition-all ${
                            isSelected 
                              ? 'bg-blue-500 text-white' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          <div className="text-xs">{dayName}</div>
                          <div className="font-bold">{dayNum}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Selected dates summary */}
            {selectedDate && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-blue-700 mb-1">
                  1 jour sélectionné
                </p>
                <p className="text-xs text-blue-600">
                  {new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                </p>
              </div>
            )}

            {/* Reason */}
            <div className="mb-4">
              <Label>Motif (optionnel)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: RDV médical, raison personnelle..."
                rows={2}
              />
            </div>

            <Button
              onClick={submitDemande}
              disabled={submitting || !selectedDate || isQuotaReachedForSelection}
              className="w-full btn-primary"
              data-testid="employee-demandes-submit-btn"
            >
              {submitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Envoyer la demande</>
              )}
            </Button>

            {isQuotaReachedForSelection && (
              <p className="mt-2 text-xs text-red-600 text-center" data-testid="employee-demandes-quota-reached-message">
                Quota mensuel atteint. Aucune nouvelle demande possible pour ce mois.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Previous requests */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Mes demandes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F172A]"></div>
              </div>
            ) : demandes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune demande en cours</p>
              </div>
            ) : (
              <div className="space-y-4">
                {demandes.map((demande) => (
                  <div
                    key={demande.demande_id}
                    className={`p-4 rounded-lg border-2 ${
                      demande.status === 'approved' ? 'bg-green-50 border-green-300' :
                      demande.status === 'rejected' ? 'bg-red-50 border-red-300' :
                      demande.status === 'cancelled' ? 'bg-slate-100 border-slate-300' :
                      demande.status === 'partial' ? 'bg-indigo-50 border-indigo-300' :
                      'bg-yellow-50 border-yellow-300'
                    }`}
                    data-testid={`employee-demande-card-${demande.demande_id}`}
                  >
                    {/* Header with global status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(demande.status)}
                        <span className={`font-semibold ${demande.status === 'rejected' ? 'text-red-700' : ''}`} data-testid={`employee-demande-status-${demande.demande_id}`}>{getStatusLabel(demande.status)}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded block" data-testid={`employee-demande-created-at-${demande.demande_id}`}>
                          Demande du {new Date(demande.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        {demande.processed_at && (
                          <span className="text-[11px] text-gray-600" data-testid={`employee-demande-processed-at-${demande.demande_id}`}>
                            Traitée le {new Date(demande.processed_at).toLocaleDateString('fr-FR')} à {new Date(demande.processed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Individual dates with their status */}
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Détail par jour :</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {demande.dates.map(date => {
                          const dateStatus = getDateStatus(demande, date);
                          return (
                            <div 
                              key={date}
                              className={`flex items-center justify-between p-2 rounded-lg ${
                                dateStatus === 'approved' ? 'bg-green-100 border border-green-300' :
                                dateStatus === 'rejected' ? 'bg-red-100 border border-red-300' :
                                dateStatus === 'partial' ? 'bg-indigo-100 border border-indigo-300' :
                                'bg-white border border-gray-200'
                              }`}
                            >
                              <span className="text-sm font-medium">
                                {new Date(date).toLocaleDateString('fr-FR', { 
                                  weekday: 'short', 
                                  day: 'numeric', 
                                  month: 'short' 
                                })}
                              </span>
                              <div className="flex items-center gap-1">
                                {getDateStatusIcon(dateStatus)}
                                <span className={`text-xs font-medium ${
                                  dateStatus === 'approved' ? 'text-green-700' :
                                  dateStatus === 'rejected' ? 'text-red-700' :
                                  dateStatus === 'cancelled' ? 'text-slate-700' :
                                  dateStatus === 'partial' ? 'text-indigo-700' :
                                  'text-yellow-700'
                                }`}>
                                  {getDateStatusLabel(dateStatus)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Reason */}
                    {demande.reason && (
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Motif :</strong> {demande.reason}
                      </p>
                    )}
                    
                    {/* Admin comment */}
                    {demande.admin_comment && demande.status !== 'rejected' && (
                      <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                        <p className="text-sm text-gray-700">
                          <strong>💬 Réponse admin :</strong> {demande.admin_comment}
                        </p>
                      </div>
                    )}

                    {getCanCancelDemande(demande) && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => cancelDemande(demande.demande_id)}
                          data-testid={`employee-demande-cancel-button-${demande.demande_id}`}
                        >
                          Annuler la demande
                        </Button>
                        <p className="text-[11px] text-gray-500 mt-1">Annulation possible uniquement pendant 24h après création.</p>
                      </div>
                    )}

                    {demande.status === 'approved' && demande.validation_info_message && (
                      <div className="mt-2 p-2 bg-emerald-50 rounded border border-emerald-200" data-testid={`employee-demande-validation-info-${demande.demande_id}`}>
                        <p className="text-sm text-emerald-800">
                          {demande.validation_info_message}
                        </p>
                      </div>
                    )}

                    {demande.history && demande.history.length > 0 && (
                      <details className="mt-3" data-testid={`employee-demande-history-${demande.demande_id}`}>
                        <summary className="text-xs font-semibold text-slate-600 cursor-pointer">Historique</summary>
                        <div className="mt-2 space-y-1">
                          {demande.history.slice().reverse().slice(0, 6).map((entry, idx) => (
                            <div key={`${entry.timestamp}-${idx}`} className="text-xs text-slate-600 bg-white rounded px-2 py-1 border border-slate-100">
                              <span className="font-semibold">{entry.action}</span>
                              {entry.action === 'cancelled_by_employee' ? ' • Demande annulée par le salarié' : ''}
                              {entry.date ? ` • ${entry.date}` : ''}
                              {entry.comment ? ` • ${entry.comment}` : ''}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DemandesPage;
